import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { take } from 'rxjs';
import { CompanyNameType, CompanyType } from '../models/company.model';
import { MasterAdminCompanyService } from '../services/master-admin-company.service';

type MasterAdminCompanyItem = CompanyNameType &
  Partial<Omit<CompanyType, 'id' | 'name'>>;

type MasterAdminCompanyState = {
  companies: MasterAdminCompanyItem[];
  selectedCompanyId: string | null;
  loading: boolean;
  saving: boolean;
  error: unknown | null;
};

const initialState: MasterAdminCompanyState = {
  companies: [],
  selectedCompanyId: null,
  loading: false,
  saving: false,
  error: null,
};

const sortCompaniesByName = <T extends CompanyNameType>(companies: T[]): T[] =>
  [...companies].sort((a, b) =>
    // The backend may return companies without a name.
    (a.name ?? '').localeCompare(b.name ?? '', undefined, {
      sensitivity: 'base',
    }),
  );

export const MasterAdminCompanyStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    hasCompanies: computed(() => store.companies().length > 0),
    selectedCompany: computed(
      () =>
        store
          .companies()
          .find((company) => company.id === store.selectedCompanyId()) ?? null,
    ),
    busy: computed(() => store.loading() || store.saving()),
  })),
  withMethods(
    (store, masteradminCompanyService = inject(MasterAdminCompanyService)) => ({
      loadCompanies(): void {
        patchState(store, { loading: true, error: null });
        masteradminCompanyService
          .getAllCompanies()
          .pipe(take(1))
          .subscribe({
            next: (companies) => {
              const selectedCompanyId = store.selectedCompanyId();
              const hasSelectedCompany = companies.some(
                (company) => company.id === selectedCompanyId,
              );
              patchState(store, {
                companies: sortCompaniesByName(companies),
                selectedCompanyId: hasSelectedCompany
                  ? selectedCompanyId
                  : null,
                loading: false,
              });
            },
            error: (error: unknown) => {
              patchState(store, { error, loading: false });
            },
          });
      },

      loadCompany(companyId: string): void {
        patchState(store, { loading: true, error: null });
        masteradminCompanyService
          .getCompany(companyId)
          .pipe(take(1))
          .subscribe({
            next: (company) => {
              const updatedCompanies = store
                .companies()
                .filter((existingCompany) => existingCompany.id !== company.id);
              patchState(store, {
                companies: sortCompaniesByName([...updatedCompanies, company]),
                selectedCompanyId: company.id,
                loading: false,
              });
            },
            error: (error: unknown) => {
              patchState(store, { error, loading: false });
            },
          });
      },

      createCompany(payload: CompanyType): void {
        patchState(store, { saving: true, error: null });
        masteradminCompanyService
          .createCompany(payload)
          .pipe(take(1))
          .subscribe({
            next: (company) => {
              const updatedCompanies = store
                .companies()
                .filter((existingCompany) => existingCompany.id !== company.id);
              patchState(store, {
                companies: sortCompaniesByName([...updatedCompanies, company]),
                selectedCompanyId: company.id,
                saving: false,
              });
            },
            error: (error: unknown) => {
              patchState(store, { error, saving: false });
            },
          });
      },

      updateCompany(companyId: string, payload: CompanyType): void {
        patchState(store, { saving: true, error: null });
        masteradminCompanyService
          .updateCompany(companyId, payload)
          .pipe(take(1))
          .subscribe({
            next: (company) => {
              const updatedCompanies = store
                .companies()
                .filter((existingCompany) => existingCompany.id !== company.id);
              patchState(store, {
                companies: sortCompaniesByName([...updatedCompanies, company]),
                selectedCompanyId: company.id,
                saving: false,
              });
            },
            error: (error: unknown) => {
              patchState(store, { error, saving: false });
            },
          });
      },

      deleteCompany(companyId: string): void {
        patchState(store, { saving: true, error: null });
        masteradminCompanyService
          .deleteCompany(companyId)
          .pipe(take(1))
          .subscribe({
            next: () => {
              patchState(store, {
                companies: store
                  .companies()
                  .filter((company) => company.id !== companyId),
                selectedCompanyId:
                  store.selectedCompanyId() === companyId
                    ? null
                    : store.selectedCompanyId(),
                saving: false,
              });
            },
            error: (error: unknown) => {
              patchState(store, { error, saving: false });
            },
          });
      },

      selectCompany(companyId: string | null): void {
        patchState(store, { selectedCompanyId: companyId, error: null });
      },

      clearError(): void {
        patchState(store, { error: null });
      },

      reset(): void {
        patchState(store, initialState);
      },
    }),
  ),
);
